/**
 * Light Theme Configuration
 * Theme definition for light mode with clean, bright colors
 */

import { tint } from 'polished'
import { ICON_COLORS } from './constants.js'
import {
  PROJECT_BADGE_TOKENS,
  ALERT_CARD_BACKGROUND,
} from './project-badge-tokens.js'

export const lightTheme = ({
  background,
  primary = '#ED2238',
  secondary = '#3361aa',
  button_primary = '#641083',
  button_secondary = '#1e293b', // Slate gray - Secondary Color
  background_color = '#02070E',
  foreground_color = '#1D282F',
  button_text = '#0a2245',
  input_border_outline = '#eff8fd',
  lninety_color = '#641083',
  highContrast = false,
  cardStyle = 'flat',
  cornerRadius = 'rounded',
}) => {
  // High contrast colors for light mode
  const highContrastBorder = '#000000' // Pure black for maximum contrast on light backgrounds
  const highContrastText = '#000000' // Pure black for maximum readability
  
  // Normal colors
  const normalBorder = '#E5E5E6'
  const normalText = '#706F76'
  
  // Select colors based on high contrast mode
  const borderColor = highContrast ? highContrastBorder : normalBorder
  const textColor = highContrast ? highContrastText : normalText
  
  return {
  embedded: {
    background_color,
    foreground_color,
    button_text,
    input_border_outline,
  },
  primary: {
    base: primary,
    medium: tint(0.5, primary),
    light: tint(0.25, primary),
  },
  secondary: {
    base: secondary,
    medium: tint(0.1, secondary),
    light: tint(0.25, secondary),
  },
  tertiary: {
    base: secondary,
    light: tint(0.25, secondary),
    medium: tint(0.2, secondary),
  },
  backgroundUrl: background,
  brand_lightest: {
    base: lninety_color,
  },
  quickLinks: {
    border: highContrast ? highContrastBorder : '#FCFCFD',
    background: '#FFFFFF',
    svgColor: '#706F76',
    textcolor: '#0C0C0D',
    shadowColor: '#7676760F',
  },
  success: {
    base: '#7EB223',
    medium: '#34C759',
    light: '#ebf9ee',
  },
  homebody: {
    base: '#004b37',
    medium: '#3d3d3d',
    light: '#39B93B',
  },
  error: { base: '#E52C32', light: '#FFEAEB' },
  warning: {
    base: '#fdf9c2',
    background: '#fffaeb',
    border: '#ffe6b0',
    text: '#b26a00',
    icon: '#ffb84e',
  },
  info: { base: '#3C88C2' },
  text: {
    dark: '#333',
    light: '#999',
    medium: '#58595b',
    white: '#1e293b',
    disabled: '#8A8A8F',
    sidebar_foreground: highContrast ? highContrastText : '#0C0C0D',
    sidebar_accent: '#9333ea',
    muted_foreground: textColor,
    footer: '#64748b',
    error: '#ff6b6b',
    payment_balance_credit: '#14b8a6',
    payment_balance_no_balance: '#f59e0b',
    payment_balance_balance_due: '#ec4899',
  },
  fonts: {
    body: `'Inter', sans-serif`,
    bold: `'Inter', sans-serif`,
    italic: `'Inter', sans-serif`,
    light: `'Inter', sans-serif`,
    regular: `'Inter', sans-serif`,
  },
  fields: {
    border: borderColor,
    border_color: borderColor,
    focus: '#476882',
  },
  gray: {
    base: '#AEB3B9',
    dark: '#263E50',
    highlight: '#ECEDEF',
    light: highContrast ? highContrastBorder : '#D8DCE0',
    medium: highContrast ? highContrastText : '#959595',
    mediumlight: '#636366',
    gray2: '#F2F2F7',
    gray3: '#D8DCE0',
    gray4: '#636366',
    gray5: '#3A3A3C',
    gray6: '#636363',
    gray7: '#E0E0E0',
    gray8: '#AEAEB3',
    gray9: '#717174',
    gray10: '#7A7A7A',
    gray11: '#5C5C70',
    gray12: '#D5D5DD',
  },
  icon: {
    base: '#3361aa',
    light: '#34518B',
    selected: '#ff0000',
    primary: '#6366f1',
    warning: '#ff9422',
    amber100: '#fef3c6',
    amber600: '#b26a00',
    lavender: '#a78bfa',
  },
  backgroundColor: {
    light: '#FFFFFF',
    white: '#1e293b',
    black: '#000',
    redish_light: '#f8f8f8',
    quote_box: '#64108308',
    tp_background: '#FCFCFC',
    tp_card: '#FFFFFF',
    dark: '#f8f3f5',
    card: '#FFFFFF',
  },
  blue: {
    base: '#3361aa',
    dark: '#233d52',
    system_blue: '#0071EB',
    blue1: '#e5f1FD',
    blue2: '#0071EB',
    blue3: '#3b71ca',
    reskin: '#2370dc',
    blue100: '#dbeafe',
    blue600: '#155dfc',
  },
  green: {
    base: '#34c759',
    light: '#ebf9ee',
    green1: '#7DF5B7',
    green2: '#7df5b71f',
    green3: '#004C3F',
    green4: '#e6f4ea',
  },
  yellow: {
    yellow1: '#FFDD57',
    yellow2: '#ffdd571f',
    yellow3: '#8b6914',
    yellow4: '#f5f1e8',
    yellow5: '#f0ead6',
  },
  red: {
    red1: '#FF6B6B',
    red2: '#ff6b6b1f',
  },
  alert: {
    overdue_bg: '#FEE2E2',
    overdue_color: '#DC2626',
    overdue_border: '#FECACA',
    attention_bg: '#FEF3C7',
    attention_color: '#D97706',
    attention_border: '#FDE68A',
  },
  project_badge: PROJECT_BADGE_TOKENS,
  alert_card_background: ALERT_CARD_BACKGROUND.light,
  priority: {
    high: '#ff6b6b',
    medium: '#ffa726',
    low: '#66bb6a',
    normal: '#42a5f5',
    high_bg: 'rgba(255, 107, 107, 0.1)',
    medium_bg: 'rgba(255, 167, 38, 0.1)',
    low_bg: 'rgba(102, 187, 106, 0.1)',
    normal_bg: 'rgba(66, 165, 245, 0.1)',
  },
  card: {
    background: '#FFFFFF',
    border: borderColor,
    hover_background: 'rgba(0, 0, 0, 0.02)',
    hover_shadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
    // Card style variations for light theme
    flat: {
      shadow: 'none',
      border: borderColor,
    },
    neumorphic: {
      shadow: 'inset 2px 2px 4px rgba(0, 0, 0, 0.1), inset -2px -2px 4px rgba(255, 255, 255, 0.8)',
      border: 'none',
    },
    elevated: {
      shadow: '0 4px 12px rgba(0, 0, 0, 0.15), 0 2px 4px rgba(0, 0, 0, 0.1)',
      border: 'none',
    },
  },
  cornerRadius: {
    sharp: '0px',
    rounded: '12px',
    extraRounded: '24px',
  },
  brown: {
    brown1: '#b1b0b5',
    brown2: '#3e3e42',
    brown3: '#262527',
    brown4: '#4e4e52',
  },
  insurance: {
    yellow: {
      dark: '#FFA800',
    },
  },
  notificationBell: {
    color: '#636366',
    background: '#FAFAFA',
    openPanelBackground: '#e0ebfa',
    notificationDot: '#da453c',
  },
  black: {
    base: '#000000',
    dark: '#050505',
    font: '#323233',
    iconText: '#0f0f0f',
    content: '#292929',
    dark1: '#232323',
  },
  embeddedView: {
    background: '#1D282F',
  },
  white: { base: '#1e293b' },
  purple: { base: '#641083', light: '#8D52CF' },
  radio_button: {
    background_blue: '#007aff',
    background_default: '#FAFAFA',
    border_color: '#D8DCE0',
    background_light_blue: '#E6F2FF',
  },
  rounded_checkbox: {
    background_blue: '#007aff',
    background_default: '#FAFAFA',
    border_color: '#D8DCE0',
  },
  button_primary: {
    background: button_primary,
    color: '#000000',
    border_color: button_primary,
  },
  button_secondary: {
    background: button_secondary,
    color: '#000000',
    border_color: button_primary,
    border: '#262527',
  },
  button_delete: {
    borderColor: '#DA453C',
    textColor: '#DA453C',
  },
  system: {
    placeholder_lable: '#D3D3D3',
  },
  tabSlider: {
    background: '#f2f2f7',
    border_color: '#e3e3e3',
    inactive_color: '#706F76',
    active_color: '#0C0C0D',
    active_background: '#1e293b',
    reskin_active_background: '#FCFCFD',
    reskin_inactive_tab_text_color: '#0F0F0F',
  },
  dashboard_quick_links: {
    color: '#636366',
    span_color: '#3A3A3C',
    custom_text_color: '#0c0c0d',
  },
  system_blue: {
    color: '#0071EB',
  },
  system_red: {
    color: '#FF3B30',
    base: '#c9241a',
  },
  reskinMenu: {
    inactive_color: '#706F76',
    sub_text_color: '#A4A3A8',
    background: '#fafafa',
    active_color: '#0C0C0D',
    sub_menu_active_color: '#636366',
    branding_logo_color: '#e42127',
    angle_icon_color: '#263E50',
    aside_bar_color: '#fafafa',
    header: '#0C0C0D',
    bgColor: '#F5F4F5',
    hover_background: 'rgba(0, 0, 0, 0.05)',
    shadow_color: '#0003'
  },
  button_selected: {
    background: '#ebedef',
    color: '#c2c3c4',
  },
  mode: {
    background_color: '#FCFCFC',
    button_text: '#1e293b',
    text_area_text_color: '#000',
    card_text_color: '#000',
    card_background_color: '#FCFCFC',
    header_background_color: '#FCFCFC',
    isDark: false,
    card_drawer_border: '#d2d7db',
    modal_background: '#1e293b',
    text_color: '#000',
    footer_tab_background_color: '#1e293b',
    footer_tab_inactive_color: '#B1B0B5',
    footer_tab_active_color: '#0C0C0D',
    footer_tab_icon_text_color: '#0F0F0F',
    footer_tab_shadow_color: '#6B6B6B14',
    info_span_color: '#000',
    alert_panel_bgColor: '#F5F4F5',
    reskinBackground: 'linear-gradient(180deg, #FCFCFD 0%, #F5F4F5 71.36%)',
    feedback_modal_border: '#f2f2f7',
    tab_background_color: '#f2f2f7',
    tab_active_background: '#FFFFFF',
    tab_inactive_background: '#f1f5f9',
    tab_hover_background_light: 'rgba(0, 0, 0, 0.05)',
    tab_hover_background_dark: 'rgba(255, 255, 255, 0.1)',
    tab_focus_shadow_light: 'rgba(0, 0, 0, 0.1)',
    tab_focus_shadow_dark: 'rgba(139, 92, 246, 0.3)',
    menu_item_hover_background: 'rgba(59, 130, 246, 0.1)',
    popover_shadow: 'rgba(0, 0, 0, 0.24)',
    side_panel_background: '#f1f5f9',
    tab_active_text_color: '#0C0C0D',
    tab_inactive_text_color: textColor,
    card_background_color: '#FFFFFF',
    card_border_color: cardStyle === 'flat' ? borderColor : 'transparent',
    card_shadow: (() => {
      const cardStyles = {
        flat: 'none',
        neumorphic: '5px 5px 10px #0003, -5px -5px 10px rgba(0, 0, 0, 0.05)',
        elevated: '0 4px 16px #0003'
      };
      return cardStyles[cardStyle] || cardStyles.flat;
    })(),
    card_border_radius: (() => {
      const radiusValues = {
        sharp: '0px',
        rounded: '12px',
        extraRounded: '24px'
      };
      // Ensure we always return a valid value, even if cornerRadius is invalid
      return radiusValues[cornerRadius] || radiusValues.rounded || '12px';
    })(),
    card_disabled_background: '#F5F4F5',
    theme_icon_light_bg: ICON_COLORS.lightIconBg,
    theme_icon_light_color: ICON_COLORS.lightIconColor,
    theme_icon_dark_bg: ICON_COLORS.darkIconBg,
    theme_icon_dark_color: ICON_COLORS.darkIconColor,
    table_background: '#FFFFFF',
    table_header_background: '#F5F4F5',
    table_text_color: '#0C0C0D',
    table_border_color: borderColor,
    table_hover_background: 'rgba(0, 0, 0, 0.02)',
    table_shadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
    status_paid_background: '#7EB223',
    status_paid_text: '#FFFFFF',
    status_unpaid_background: '#FF3B30',
    status_unpaid_text: '#FFFFFF',
    status_other_background: '#FF8707',
    status_other_text: '#FFFFFF',
  },
  system_message: {
    info: {
      background_color: '#E5F0FF',
      color: '#2672DC',
    },
    error: {
      background_color: '#FFE7E5',
      color: '#DA453C',
    },
    success: {
      background_color: '#E5FFE6',
      color: '#39B93B',
    },
    warning: {
      background_color: '#FCF8E8',
      color: '#D2A428',
    },
    font: '#0F0F0F',
  },
  loader: {
    color: '#0C0C0D',
  },
  sub_menu: {
    background: '#f5f4f5',
    icon_color: '#FCFCFD',
    icon_background_color: '#0C0C0D',
    title_color: '#0C0C0D',
    description_color: '#706F76',
    border_color: borderColor,
  },
  clubs: {
    clubTitle: '#0C0C0D',
    clubDescription: '#0C0C0D',
    joinClub: '#2370DC',
    pendingApproval: '#FDF4CE',
    pendingApprovalText: '#0C0C0D',
    buttonDivBackground: '#f5f4f5',
    buttonDivBorderColor: borderColor,
  },
  rightSidePanel: {
    imageTextColor: '#FCFCFD',
    sliderActiveDot: '#0C0C0D',
    sliderInActiveDot: '#B1B0B5',
    textColor: '#0C0C0D',
    subTextColor: '#706F76',
    locationColor: '#2370DC',
    bottomActionLineColor: borderColor,
    date_color: '#706F76',
    pendingApproval: '#FDF4CE',
    pendingApprovalText: '#0C0C0D',
  },
  paymentHeader: {
    background: '#FCFCFD',
    border: borderColor,
  },
  menuPageHeader: {
    borderColor: borderColor,
    backgroundColor: '#F5F4F5',
  },
  amenities: {
    titleText: '#0c0c0d',
    dateTimeText: '#706F76',
    background_color: '#FCFCFD',
  },
  columnAds: {
    content_color: '#0C0C0D',
    background: '#FCFCFD',
    carousalActive: '#0C0C0D',
    carousalInactive: '#A4A3A8',
    iconBackground: '#D8D8DA',
    seperator: '#A4A3A8',
  },
  scrollbars: {
    width: 'thin',
    color: 'rgba(0, 0, 0, 0.3) transparent',
    footer_tab_active_color: '#0C0C0D',
    footer_tab_icon_text_color: '#0F0F0F',
    footer_tab_shadow_color: '#6B6B6B14',
    info_span_color: '#000',
    alert_panel_bgColor: '#F5F4F5',
    reskinBackground: 'linear-gradient(180deg, #FCFCFD 0%, #F5F4F5 71.36%)',
  },
  }
}

